import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, Search, Filter, MapPin, Calendar, DollarSign, 
  X, ChevronDown, ChevronUp, Loader2, Grid, List
} from 'lucide-react';
import { jobsData, Job } from '../data/jobs';
import './Jobs.css';

interface FilterState {
  location: string;
  searchQuery: string;
  minPay: string;
  maxPay: string;
}

// Simple Job Card Component
const SimpleJobCard: React.FC<{ job: Job; index: number }> = ({ job, index }) => {
  return (
    <motion.div
      key={job.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-300 ${
        !job.is_active ? 'opacity-60' : ''
      }`}
    >
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          job.is_active 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          {job.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>
      
      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
        <div className="flex items-center space-x-1">
          <MapPin className="w-4 h-4" />
          <span>{job.location}</span>
        </div>
        <div className="flex items-center space-x-1">
          <DollarSign className="w-4 h-4" />
          <span>{job.pay}</span>
        </div>
        <div className="flex items-center space-x-1">
          <Calendar className="w-4 h-4" />
          <span>{job.duration}</span>
        </div>
      </div>

      <p className="text-gray-700 mb-4">
        {job.description.length > 150 
          ? `${job.description.substring(0, 150)}...` 
          : job.description
        }
      </p>

      {job.requirements && job.requirements.length > 0 && (
        <div className="mb-4">
          <h4 className="font-medium text-sm text-gray-800 mb-2">Requirements:</h4>
          <div className="flex flex-wrap gap-1">
            {job.requirements.slice(0, 3).map((req, idx) => (
              <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                {req}
              </span>
            ))}
            {job.requirements.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                +{job.requirements.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-500">
          Posted {new Date(job.created_at).toLocaleDateString()}
        </span>
        <div className="flex space-x-2">
          {job.is_active && (
            <a
              href={job.link}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Apply Now
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const Jobs: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    location: '',
    searchQuery: '',
    minPay: '',
    maxPay: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    // Load jobs from local data
    setJobs(jobsData);
    setLoading(false);
    
    // Extract unique locations
    const uniqueLocations = Array.from(new Set(jobsData.map(job => job.location)));
    setLocations(uniqueLocations);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [jobs, filters]);

  const applyFilters = () => {
    let filtered = [...jobs];

    // Search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(query) ||
        job.description.toLowerCase().includes(query) ||
        job.location.toLowerCase().includes(query)
      );
    }

    // Location filter
    if (filters.location) {
      filtered = filtered.filter(job => 
        job.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Pay range filter (basic implementation)
    if (filters.minPay || filters.maxPay) {
      filtered = filtered.filter(job => {
        const payValue = parseInt(job.pay.replace(/[^0-9]/g, ''));
        const minPay = filters.minPay ? parseInt(filters.minPay) : 0;
        const maxPay = filters.maxPay ? parseInt(filters.maxPay) : Infinity;
        return payValue >= minPay && payValue <= maxPay;
      });
    }

    // Separate active and inactive jobs
    const activeJobs = filtered.filter(job => job.is_active);
    const inactiveJobs = filtered.filter(job => !job.is_active);
    
    // Sort active jobs by creation date (newest first)
    activeJobs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    
    // Combine: active jobs first, then inactive jobs
    setFilteredJobs([...activeJobs, ...inactiveJobs]);
  };

  const handleSearch = (query: string) => {
    setFilters(prev => ({ ...prev, searchQuery: query }));
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      searchQuery: '',
      minPay: '',
      maxPay: ''
    });
  };

  const hasActiveFilters = filters.location || filters.searchQuery || filters.minPay || filters.maxPay;

  return (
    <div className="jobs-page">
      {/* Header */}
      <section className="jobs-header">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
                <Briefcase className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Find Your Perfect Gig
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Browse hundreds of one-time job opportunities across New York City. 
              From events to deliveries, find work that fits your schedule.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="search-filters-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            {/* Search Bar */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search jobs by title, description, or location..."
                  value={filters.searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
              >
                <Filter className="w-4 h-4" />
                Filters
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 rounded-xl transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 rounded-xl transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Filters */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="border-t border-gray-200 pt-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Location Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <select
                      value={filters.location}
                      onChange={(e) => handleFilterChange('location', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Locations</option>
                      {locations.map(location => (
                        <option key={location} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Min Pay Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Pay
                    </label>
                    <input
                      type="number"
                      placeholder="Min $"
                      value={filters.minPay}
                      onChange={(e) => handleFilterChange('minPay', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Max Pay Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Pay
                    </label>
                    <input
                      type="number"
                      placeholder="Max $"
                      value={filters.maxPay}
                      onChange={(e) => handleFilterChange('maxPay', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Clear Filters
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="results-section">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Results Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {hasActiveFilters ? 'Search Results' : 'Latest Job Opportunities'}
              </h2>
              <p className="text-gray-600">
                {filteredJobs.length} {filteredJobs.length === 1 ? 'job' : 'jobs'} found
                {hasActiveFilters && (
                  <span className="ml-2 text-sm text-gray-500">
                    (filtered from {jobs.length} total)
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 text-center">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && jobs.length === 0 && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          )}

          {/* No Results */}
          {!loading && filteredJobs.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {hasActiveFilters ? 'No jobs found' : 'No jobs available'}
              </h3>
              <p className="text-gray-600 mb-6">
                {hasActiveFilters 
                  ? 'Try adjusting your search criteria or check back later for new opportunities.'
                  : 'Check back soon for new job opportunities!'
                }
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  View all jobs
                </button>
              )}
            </div>
          )}

          {/* Jobs Grid/List */}
          {!loading && filteredJobs.length > 0 && (
            <div>
              {/* Active Jobs */}
              {filteredJobs.filter(job => job.is_active).length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Active Jobs ({filteredJobs.filter(job => job.is_active).length})
                  </h3>
                  <div className={`${
                    viewMode === 'grid' 
                      ? 'grid gap-6 md:grid-cols-2 lg:grid-cols-3' 
                      : 'space-y-4'
                  }`}>
                    {filteredJobs.filter(job => job.is_active).map((job, index) => (
                      <SimpleJobCard key={job.id} job={job} index={index} />
                    ))}
                  </div>
                </div>
              )}

              {/* Inactive Jobs */}
              {filteredJobs.filter(job => !job.is_active).length > 0 && (
                <div className="opacity-60">
                  <h3 className="text-xl font-semibold text-gray-600 mb-4">
                    Inactive Jobs ({filteredJobs.filter(job => !job.is_active).length})
                  </h3>
                  <div className={`${
                    viewMode === 'grid' 
                      ? 'grid gap-6 md:grid-cols-2 lg:grid-cols-3' 
                      : 'space-y-4'
                  }`}>
                    {filteredJobs.filter(job => !job.is_active).map((job, index) => (
                      <SimpleJobCard key={job.id} job={job} index={index} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </section>
    </div>
  );
};

export default Jobs;